/** @format */

import {
  Chk,
  DataFiles,
  Feature,
  Logging,
  Loyalty,
  MyAccount,
  Pricing,
  Tests,
} from './environment-common-config.interface';

import { API } from './environment-api.interface';
import { ApiTokens } from './environment-api-tokens.interface';
import { BaseUrls } from './environment-base-urls.interface';
import { LaunchConfig } from './launch-config.interface';
import { ThirdParty } from './environment-third-party.interface';

/**
 * Bounds for a time-limited promotion, expressed as absolute UTC instants.
 *
 * Both values must carry an explicit offset (`Z` or `±HH:MM`). A string without one is
 * interpreted as UTC by the date parser, so `2026-09-24T00:00:00` would silently resolve to
 * 5pm PT the previous day. Date-only values are rejected for the same reason.
 */
export interface PromoWindowConfig {
  /** Inclusive start, e.g. `2026-09-24T07:00:00Z` (midnight PT during daylight time). */
  start: string;
  /** Exclusive end, e.g. `2026-09-28T07:00:00Z`. */
  end: string;
}

/** Per-environment promotion windows, keyed by promotion name. */
export interface PromoWindows {
  exclusiveBrandCoupons?: PromoWindowConfig;
  limitedTimeCreditAcquisitionOffer?: PromoWindowConfig;
  scorecardExclusiveBrandsHolidayOffer?: PromoWindowConfig;
}

export interface IEnvironment {
  api: API;

  apiAuthKeys?: {
    partnerKey: string;
    secretKey: string;
  };
  apiTokens?: ApiTokens;
  baseDomain?: Record<string, string>;
  baseUrls?: BaseUrls;
  captchaSiteKey?: string;
  catOvrGrpId?: Record<string, string>;
  chk?: Chk;
  customizer?: string;
  dataFiles?: DataFiles;
  defaultActiveCloudProvider?: string;
  environmentName?: string;
  esbEnvironmentHeader?: string;
  feature: Feature;
  handshakesParams?: Record<string, string>;
  identityServiceKey?: {
    partnerKey: string;
    secretKey: string;
  };
  imagePresets?: boolean;
  inventory?: Record<string, number>;
  isLocal?: boolean;
  // Killswitches in clp have explicit structure than interface.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  killSwitch?: any;
  launchConfig?: LaunchConfig;
  liveChatLinks?: Record<string, string>;
  logging?: Logging;
  loyalty?: Loyalty;
  myAccount?: MyAccount;
  offerDate?: {
    endDateString: string;
    startDateString: string;
  };
  partyAuthKeys?: {
    partnerKey: string;
    secretKey: string;
  };
  platform?: {
    forwardedHeadersMatchers?: RegExp[];
  };
  previewLinks?: boolean;
  pricing?: Pricing;
  production?: boolean;
  promoWindows?: PromoWindows;
  searchbyTermTypeParam?: string;
  simpleHeaderLogoUrl?: {
    prod: Record<string, string>;
    nonProd: Record<string, string>;
  };
  solePanel?: {
    dsg: string;
    gg: string;
    iframeParent: string;
    mj?: string;
    pl: string;
  };
  tests?: Tests;
  thirdParty: ThirdParty;
  trackOrders?: boolean;
  useRedis?: boolean;
  images?: {
    netstorage: {
      marketing: string;
      product: string;
    };
    scene7proxy: {
      marketing: string;
      marketingContent: string;
      product: string;
    };
  };
  echt?: {
    dsg: string;
    gg: string;
    g3: string;
    pl: string;
  };
}

export class Environment implements IEnvironment {
  declare api: API;
  declare apiAuthKeys?: {
    partnerKey: string;
    secretKey: string;
  };

  declare apiTokens?: ApiTokens;
  declare baseUrls?: BaseUrls;
  declare catOvrGrpId?: Record<string, string>;
  declare customizer?: string;
  declare dataFiles?: IEnvironment['dataFiles'];
  declare defaultActiveCloudProvider?: string;
  declare environmentName?: string;
  declare esbEnvironmentHeader?: string;
  declare inventory?: Record<string, number>;
  declare chk?: Chk;
  declare logging?: Logging;
  declare myAccount?: MyAccount;
  declare loyalty?: Loyalty;
  declare pricing?: Pricing;
  declare production?: boolean;
  declare searchbyTermTypeParam?: string;
  declare thirdParty: ThirdParty;
  declare feature: Feature;
  // Killswitches in clp have explicit structure than interface.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  declare killSwitch?: any;
  declare imagePresets?: boolean;
  declare useRedis?: boolean;
  declare previewLinks?: boolean;
  declare tests?: Tests;
  declare launchConfig?: LaunchConfig;
  declare handshakesParams?: Record<string, string>;
  declare baseDomain?: Record<string, string>;
  declare trackOrders?: boolean;
  declare echt?: {
    dsg: string;
    gg: string;
    g3: string;
    pl: string;
  };

  identityServiceKey?: {
    partnerKey: string;
    secretKey: string;
  };

  creditServiceKey?: {
    partnerKey: string;
    secretKey: string;
  };

  offerDate?: {
    endDateString: string;
    startDateString: string;
  };

  declare promoWindows?: PromoWindows;

  scorecardPlus?: {
    fallbackImageIdentifier: string;
  };

  declare partyAuthKeys?: {
    partnerKey: string;
    secretKey: string;
  };

  public isLocal = false;
  declare solePanel?: {
    dsg: string;
    gg: string;
    iframeParent: string;
    mj: string;
    pl: string;
  };

  declare liveChatLinks?: Record<string, string>;

  declare platform?: {
    forwardedHeadersMatchers?: RegExp[];
  };

  declare simpleHeaderLogoUrl?: {
    prod: Record<string, string>;
    nonProd: Record<string, string>;
  };
}
